import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash';
import {
  ColType,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  DEFAULT_FUND_SUMMARY_COLUMNS,
  SpreadsheetDefinition,
  SummaryDefinition, SummaryVisibility
} from 'common/types/summaryDefinitions';
import {
  useCreateFirmDefinition,
  useCreateFundDefinition,
  useDeleteFirmDefinition,
  useDeleteFundDefinition,
  useGetFirmDefinitions,
  useGetFundDefinitions,
  useUpdateFirmDefinitionsOrder,
  useUpdateFundDefinitionsOrder,
} from 'services/hooks/summaryDefinitions';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import { Logger } from 'utilities';
import {
  OrderedDefinition,
  SUMMARY_TYPE_FIRM,
  SUMMARY_TYPE_FUND,
  SummaryDefinitionsContextValue,
  SummaryDefinitionsProviderProps,
  SummaryType,
} from './types';

const SummaryDefinitionsContext = createContext<SummaryDefinitionsContextValue | null>(null);

/**
 * Helper to merge and order definitions
 */
function computeOrderedDefinitions(
  definitions: SummaryDefinition[],
  globalDefinitions: SummaryDefinition[],
  order: number[]
): OrderedDefinition[] {
  const customById = new Map(definitions.map(d => [d.id, d]));

  const result: OrderedDefinition[] = [];

  // Add items in the specified order
  order.forEach(id => {
    const customDef = customById.get(id);
    if (customDef) {
      result.push({ ...customDef, isGlobal: false });
      customById.delete(id);
    }
  });

  // Add remaining custom definitions not in order
  customById.forEach(def => {
    result.push({ ...def, isGlobal: false });
  });

  // Add global definitions at the end
  globalDefinitions.forEach(def => {
    result.push({ ...def, isGlobal: true });
  });

  return result;
}

/**
 * Helper to get default spreadsheet definition
 */
function getDefaultSpreadsheetDefinition(summaryType: SummaryType): SpreadsheetDefinition {
  const columns = summaryType === SUMMARY_TYPE_FIRM ? DEFAULT_FIRM_SUMMARY_COLUMNS : DEFAULT_FUND_SUMMARY_COLUMNS;

  return {
    columns: Object.fromEntries(columns.map((col, idx) => [idx, col])),
    sortKeys: [],
    groupBy: 'company',
    filters: [],
    filterExclusions: [],
  };
}

/**
 * Provider component for summary definitions state management
 */
export const SummaryDefinitionsProvider = ({
  children,
  summaryType,
  entityId,
  initialActiveDefinitionId = null,
  fetchEnabled = true,
}: SummaryDefinitionsProviderProps) => {
  const isFirmSummary = summaryType === SUMMARY_TYPE_FIRM;

  // Fetch definitions based on summary type
  const firmQuery = useGetFirmDefinitions({
    firmId: entityId,
    enabled: isFirmSummary && Boolean(entityId) && fetchEnabled,
  });
  const fundQuery = useGetFundDefinitions({
    fundId: entityId,
    enabled: summaryType === SUMMARY_TYPE_FUND && Boolean(entityId) && fetchEnabled,
  });

  const query = isFirmSummary ? firmQuery : fundQuery;

  // Mutations
  const createFirmMutation = useCreateFirmDefinition(entityId);
  const createFundMutation = useCreateFundDefinition(entityId);
  const createMutation = isFirmSummary ? createFirmMutation : createFundMutation;

  const deleteFirmMutation = useDeleteFirmDefinition(entityId);
  const deleteFundMutation = useDeleteFundDefinition(entityId);
  const deleteMutation = isFirmSummary ? deleteFirmMutation : deleteFundMutation;

  const updateOrderFirmMutation = useUpdateFirmDefinitionsOrder(entityId);
  const updateOrderFundMutation = useUpdateFundDefinitionsOrder(entityId);
  const updateOrderMutation = isFirmSummary ? updateOrderFirmMutation : updateOrderFundMutation;

  // Local state
  const [activeDefinitionId, setActiveDefinitionId] = useState<number | null>(initialActiveDefinitionId);
  const [pendingChanges, setPendingChanges] = useState<Partial<SpreadsheetDefinition> | null>(null);

  // Computed state - memoize to prevent unnecessary re-renders
  const definitions = useMemo(() => query.data?.definitions ?? [], [query.data?.definitions]);
  const globalDefinitions = useMemo(
    () => query.data?.global_definitions ?? [],
    [query.data?.global_definitions]
  );
  const order = useMemo(() => query.data?.order ?? [], [query.data?.order]);

  const orderedDefinitions = useMemo(
    () => computeOrderedDefinitions(definitions, globalDefinitions, order),
    [definitions, globalDefinitions, order]
  );

  const activeDefinition = useMemo(() => {
    if (isNil(activeDefinitionId)) {
      return null;
    }
    return orderedDefinitions.find(d => d.id === activeDefinitionId) ?? null;
  }, [activeDefinitionId, orderedDefinitions]);

  const hasUnsavedChanges = pendingChanges !== null;

  const summaryDefinitionsService = useMemo(() => new SummaryDefinitionsService(), []);

  // Set initial active definition to first available
  useEffect(() => {
    if (!activeDefinitionId && orderedDefinitions.length > 0) {
      setActiveDefinitionId(orderedDefinitions[0].id);
    }
  }, [activeDefinitionId, orderedDefinitions]);

  // Actions
  const createDefinition = useCallback(
    async (
      name: string,
      spreadsheetDefinition?: SpreadsheetDefinition,
      visibility: SummaryVisibility = 'firm',
      clonedFrom?: number | null
    ): Promise<SummaryDefinition> => {
      const baseDefinition = spreadsheetDefinition ?? getDefaultSpreadsheetDefinition(summaryType);
      const normalizedDefinition: SpreadsheetDefinition = {
        ...getDefaultSpreadsheetDefinition(summaryType),
        ...baseDefinition,
        columns: baseDefinition.columns ?? {},
        sortKeys: baseDefinition.sortKeys ?? [],
        groupBy: baseDefinition.groupBy ?? 'company',
        filters: baseDefinition.filters ?? [],
        filterExclusions: baseDefinition.filterExclusions ?? [],
      };

      const result = await createMutation.mutateAsync({
        name,
        spreadsheet_definition: normalizedDefinition,
        visibility,
        ...(isNil(clonedFrom) ? {} : { cloned_from: clonedFrom }),
      });
      return result;
    },
    [createMutation, summaryType]
  );

  const updateDefinition = useCallback(
    async (
      definitionId: number,
      updates: Partial<{ name: string; spreadsheet_definition: SpreadsheetDefinition; visibility: SummaryVisibility }>
    ): Promise<SummaryDefinition> => {
      if (summaryType === SUMMARY_TYPE_FIRM) {
        return summaryDefinitionsService.updateFirmDefinition(entityId, definitionId, updates);
      }

      return summaryDefinitionsService.updateFundDefinition(entityId, definitionId, updates);
    },
    [entityId, summaryDefinitionsService, summaryType]
  );

  const deleteDefinition = useCallback(
    async (definitionId: number): Promise<void> => {
      await deleteMutation.mutateAsync(definitionId);
      if (activeDefinitionId === definitionId) {
        const remaining = orderedDefinitions.filter(d => d.id !== definitionId);
        setActiveDefinitionId(remaining.length > 0 ? remaining[0].id : null);
      }
    },
    [deleteMutation, activeDefinitionId, orderedDefinitions]
  );

  const cloneDefinition = useCallback(
    async (sourceDefinitionId: number, newName: string): Promise<SummaryDefinition> => {
      const sourceDefinition = orderedDefinitions.find(definition => definition.id === sourceDefinitionId);

      if (!sourceDefinition) {
        throw new Error(`Unable to find summary definition with id ${sourceDefinitionId}`);
      }

      const spreadsheetDefinition =
        sourceDefinition.spreadsheet_definition ??
        sourceDefinition.spreadsheetDefinition ??
        getDefaultSpreadsheetDefinition(summaryType);

      const isGlobalDefinition = (sourceDefinition as OrderedDefinition).isGlobal;
      const clonedFrom = isGlobalDefinition ? sourceDefinition.id : sourceDefinition.cloned_from ?? null;
      const visibility: SummaryVisibility = 'firm';

      return createDefinition(newName, spreadsheetDefinition, visibility, clonedFrom);
    },
    [createDefinition, orderedDefinitions, summaryType]
  );

  const updateOrder = useCallback(
    async (newOrder: number[]): Promise<void> => {
      await updateOrderMutation.mutateAsync({ order: newOrder });
    },
    [updateOrderMutation]
  );

  const moveDefinition = useCallback(
    async (definitionId: number, direction: 'up' | 'down'): Promise<void> => {
      const currentIndex = order.indexOf(definitionId);
      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= order.length) {
        return;
      }

      const newOrder = [...order];
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

      await updateOrder(newOrder);
    },
    [order, updateOrder]
  );

  // Column management
  const addColumn = useCallback(
    (columnType: ColType, index?: number) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      const currentColumns = { ...currentDef.columns };
      const columnValues = Object.values(currentColumns);

      if (columnValues.includes(columnType)) {
        Logger.warn(`Column type ${columnType} already exists`);
        return;
      }

      const existingValues = Object.entries(currentColumns)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, colType]) => colType);

      const insertIndex = index ?? existingValues.length;

      // Insert the new column at the specified index
      const newValues = [
        ...existingValues.slice(0, insertIndex),
        columnType,
        ...existingValues.slice(insertIndex),
      ];

      const newColumns = Object.fromEntries(
        newValues.map((col, idx) => [idx, col])
      ) as Record<number, ColType>;

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const removeColumn = useCallback(
    (columnType: ColType) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      const filteredValues = Object.entries(currentDef.columns)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, colType]) => colType)
        .filter(col => col !== columnType);

      const newColumns = Object.fromEntries(
        filteredValues.map((col, idx) => [idx, col])
      ) as Record<number, ColType>;

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const reorderColumns = useCallback(
    (newColumns: Record<number, ColType>) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const setSortKeys = useCallback(
    (sortKeys: SpreadsheetDefinition['sortKeys']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        sortKeys,
      }));
    },
    [activeDefinition]
  );

  const setGroupBy = useCallback(
    (groupBy: SpreadsheetDefinition['groupBy']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        groupBy,
      }));
    },
    [activeDefinition]
  );

  const setFilters = useCallback(
    (filters: SpreadsheetDefinition['filters']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        filters,
      }));
    },
    [activeDefinition]
  );

  const saveChanges = useCallback(async () => {
    if (!activeDefinition || !pendingChanges) {
      return;
    }

    const currentDef = activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition;
    const mergedDef: SpreadsheetDefinition = {
      ...currentDef,
      ...pendingChanges,
    } as SpreadsheetDefinition;

    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: mergedDef,
    });

    setPendingChanges(null);
    query.refetch();
  }, [activeDefinition, pendingChanges, updateDefinition, query]);

  const discardChanges = useCallback(() => {
    setPendingChanges(null);
  }, []);

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  // Build memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo<SummaryDefinitionsContextValue>(
    () => ({
      // State
      definitions,
      globalDefinitions,
      order,
      orderedDefinitions,
      activeDefinitionId,
      activeDefinition,
      hasUnsavedChanges,
      pendingChanges,
      isLoading: query.isLoading,
      isSaving: createMutation.isPending || deleteMutation.isPending || updateOrderMutation.isPending,
      error: query.error,
      // Actions
      createDefinition,
      updateDefinition,
      deleteDefinition,
      cloneDefinition,
      updateOrder,
      moveDefinition,
      setActiveDefinition: setActiveDefinitionId,
      addColumn,
      removeColumn,
      reorderColumns,
      setSortKeys,
      setGroupBy,
      setFilters,
      saveChanges,
      discardChanges,
      refetch,
    }),
    [
      definitions,
      globalDefinitions,
      order,
      orderedDefinitions,
      activeDefinitionId,
      activeDefinition,
      hasUnsavedChanges,
      pendingChanges,
      query.isLoading,
      query.error,
      createMutation.isPending,
      deleteMutation.isPending,
      updateOrderMutation.isPending,
      createDefinition,
      updateDefinition,
      deleteDefinition,
      cloneDefinition,
      updateOrder,
      moveDefinition,
      setActiveDefinitionId,
      addColumn,
      removeColumn,
      reorderColumns,
      setSortKeys,
      setGroupBy,
      setFilters,
      saveChanges,
      discardChanges,
      refetch,
    ]
  );

  return (
    <SummaryDefinitionsContext.Provider value={contextValue}>{children}</SummaryDefinitionsContext.Provider>
  );
}

/**
 * Hook to access summary definitions context
 */
export function useSummaryDefinitions(): SummaryDefinitionsContextValue {
  const context = useContext(SummaryDefinitionsContext);
  if (!context) {
    throw new Error('useSummaryDefinitions must be used within a SummaryDefinitionsProvider');
  }
  return context;
}

export default SummaryDefinitionsContext;
